import React from 'react';
import {
  TrendingUp, TrendingDown, Activity, BarChart2,
  CheckCircle, XCircle, AlertCircle
} from 'react-feather';

interface SatisfactionData {
  totalResponses: number;
  yesCount: number;
  partialCount: number;
  noCount: number;
  skipCount: number;
  escalationCount: number;
  timeSeriesData?: Array<{
    date: string;
    yes: number;
    partial: number;
    no: number;
    skip: number;
  }>;
}

interface Props {
  data: SatisfactionData;
  period?: 'day' | 'week' | 'month';
}

const SatisfactionAnalytics: React.FC<Props> = ({ data, period: _period = 'week' }) => {
  void _period; // Available for future period-based filtering UI

  // Calculate percentages
  const calculatePercentage = (count: number) => {
    if (data.totalResponses === 0) return 0;
    return Math.round((count / data.totalResponses) * 100);
  };

  const satisfactionRate = calculatePercentage(data.yesCount);
  const partialRate = calculatePercentage(data.partialCount);
  const dissatisfactionRate = calculatePercentage(data.noCount);
  const skipRate = calculatePercentage(data.skipCount);

  // Calculate escalation rate
  const escalationRate = data.totalResponses > 0
    ? Math.round((data.escalationCount / data.totalResponses) * 100)
    : 0;

  // Determine trend (mock data for now)
  const trend = satisfactionRate > 70 ? 'up' : satisfactionRate < 50 ? 'down' : 'stable';

  return (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="text-green-600" size={24} />
            </div>
            {trend === 'up' && <TrendingUp className="text-green-500" size={20} />}
            {trend === 'down' && <TrendingDown className="text-red-500" size={20} />}
          </div>
          <p className="text-3xl font-bold text-gray-900">{satisfactionRate}%</p>
          <p className="text-sm text-gray-600 mt-1">Satisfaction Rate</p>
          <p className="text-xs text-gray-500 mt-2">{data.yesCount} fully satisfied</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <AlertCircle className="text-yellow-600" size={24} />
            </div>
          </div>
          <p className="text-3xl font-bold text-gray-900">{partialRate}%</p>
          <p className="text-sm text-gray-600 mt-1">Partial Satisfaction</p>
          <p className="text-xs text-gray-500 mt-2">{data.partialCount} partially satisfied</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-2 bg-red-100 rounded-lg">
              <XCircle className="text-red-600" size={24} />
            </div>
          </div>
          <p className="text-3xl font-bold text-gray-900">{dissatisfactionRate}%</p>
          <p className="text-sm text-gray-600 mt-1">Not Satisfied</p>
          <p className="text-xs text-gray-500 mt-2">{data.noCount} not satisfied</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Activity className="text-orange-600" size={24} />
            </div>
          </div>
          <p className="text-3xl font-bold text-gray-900">{escalationRate}%</p>
          <p className="text-sm text-gray-600 mt-1">Escalation Rate</p>
          <p className="text-xs text-gray-500 mt-2">{data.escalationCount} escalated</p>
        </div>
      </div>

      {/* Satisfaction Breakdown */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Satisfaction Breakdown</h3>
          <BarChart2 className="text-gray-400" size={20} />
        </div>
        
        <div className="space-y-4">
          {/* Yes Bar */}
          <div>
            <div className="flex justify-between items-center mb-1">
              <span className="text-sm font-medium text-gray-700">Fully Satisfied</span>
              <span className="text-sm text-gray-600">{data.yesCount} ({satisfactionRate}%)</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-green-500 h-2 rounded-full transition-all duration-500"
                style={{ width: `${satisfactionRate}%` }}
              />
            </div>
          </div>

          {/* Partial Bar */}
          <div>
            <div className="flex justify-between items-center mb-1">
              <span className="text-sm font-medium text-gray-700">Partially Satisfied</span>
              <span className="text-sm text-gray-600">{data.partialCount} ({partialRate}%)</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-yellow-500 h-2 rounded-full transition-all duration-500"
                style={{ width: `${partialRate}%` }}
              />
            </div>
          </div>

          {/* No Bar */}
          <div>
            <div className="flex justify-between items-center mb-1">
              <span className="text-sm font-medium text-gray-700">Not Satisfied</span>
              <span className="text-sm text-gray-600">{data.noCount} ({dissatisfactionRate}%)</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-red-500 h-2 rounded-full transition-all duration-500"
                style={{ width: `${dissatisfactionRate}%` }}
              />
            </div>
          </div>

          {/* Skip Bar */}
          <div>
            <div className="flex justify-between items-center mb-1">
              <span className="text-sm font-medium text-gray-700">Skipped</span>
              <span className="text-sm text-gray-600">{data.skipCount} ({skipRate}%)</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-gray-400 h-2 rounded-full transition-all duration-500"
                style={{ width: `${skipRate}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Insights and Recommendations */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Insights & Recommendations</h3>
        <div className="space-y-3">
          {satisfactionRate >= 80 && (
            <div className="flex items-start space-x-3">
              <CheckCircle className="text-green-500 mt-0.5" size={20} />
              <div>
                <p className="text-sm font-medium text-gray-900">Excellent Satisfaction Rate</p>
                <p className="text-sm text-gray-600">
                  Your AI assistant is performing well with {satisfactionRate}% satisfaction rate.
                </p>
              </div>
            </div>
          )}
          
          {partialRate >= 30 && (
            <div className="flex items-start space-x-3">
              <AlertCircle className="text-yellow-500 mt-0.5" size={20} />
              <div>
                <p className="text-sm font-medium text-gray-900">High Partial Satisfaction</p>
                <p className="text-sm text-gray-600">
                  {partialRate}% of users are partially satisfied. Consider reviewing these conversations 
                  to identify areas for improvement in response completeness.
                </p>
              </div>
            </div>
          )}
          
          {dissatisfactionRate >= 20 && (
            <div className="flex items-start space-x-3">
              <XCircle className="text-red-500 mt-0.5" size={20} />
              <div>
                <p className="text-sm font-medium text-gray-900">Action Needed</p>
                <p className="text-sm text-gray-600">
                  {dissatisfactionRate}% dissatisfaction rate requires attention. Review negative feedback 
                  to identify common issues and update training data accordingly.
                </p>
              </div>
            </div>
          )}
          
          {escalationRate >= 15 && (
            <div className="flex items-start space-x-3">
              <Activity className="text-orange-500 mt-0.5" size={20} />
              <div>
                <p className="text-sm font-medium text-gray-900">High Escalation Rate</p>
                <p className="text-sm text-gray-600">
                  {escalationRate}% of conversations are being escalated. Analyze escalated tickets 
                  to identify knowledge gaps in the AI assistant.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SatisfactionAnalytics;